/**
 * The ONLY `import.meta.env` reader in this workspace.
 *
 * Vite inlines `import.meta.env.VITE_*` at build time, so the server-side
 * "parse a zod schema at boot" pattern the other workspaces use cannot apply
 * here — a dynamic key lookup compiles to `undefined`. Every variable is
 * therefore read literally, once, and exported as a resolved `as const` object.
 *
 * Renames from the Next.js client: `NEXT_PUBLIC_*` became `VITE_*`. Values are
 * otherwise unchanged, and `.env.example` lists the old name beside each one.
 */
import type { SuiNetwork } from "@/lib/sui";

/** Empty string and unset are the same thing for a Vite-inlined variable. */
const str = (value: string | undefined, fallback: string): string => {
  const trimmed = (value ?? "").trim();

  return trimmed.length > 0 ? trimmed : fallback;
};

/** Optional variable: blank placeholders in `.env.local` read as "unset". */
const opt = (value: string | undefined): string | undefined => {
  const trimmed = (value ?? "").trim();

  return trimmed.length > 0 ? trimmed : undefined;
};

const num = (value: string | undefined, fallback: number): number => {
  const parsed = Number((value ?? "").trim());

  return Number.isFinite(parsed) ? parsed : fallback;
};

/** `undefined` rather than an Invalid Date, so call sites can branch on it. */
const date = (value: string | undefined): Date | undefined => {
  const raw = opt(value);

  if (!raw) return undefined;

  const parsed = new Date(raw);

  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const network = ((): SuiNetwork => {
  const raw = str(import.meta.env.VITE_SUI_NETWORK, "mainnet");

  return raw === "testnet" || raw === "devnet" ? raw : "mainnet";
})();

export const config = {
  /**
   * Active SUI network. Was `NEXT_PUBLIC_DEFAULT_SUI_NETWORK`, which also sits
   * in the shared server-side env slice — the two MUST agree, or the browser
   * signs against one network while the API verifies against another.
   */
  suiNetwork: network,

  /**
   * gRPC endpoint override. Unset means the public fullnode for `suiNetwork`
   * (see `src/lib/sui/grpc.ts`). Was `NEXT_PUBLIC_SUI_GRPC_URL`.
   */
  suiGrpcUrl: opt(import.meta.env.VITE_SUI_GRPC_URL),

  /** Block-explorer origin; paths are appended by `useRoutes().sui.explorerUrl`. */
  suiExplorerUrl: str(import.meta.env.VITE_SUI_EXPLORER_URL, "https://suivision.xyz"),

  /** License-sale Move package. Every PTB target string is built from it. */
  packageId: str(import.meta.env.VITE_PACKAGE_ID, ""),

  /** Shared `Treasury` object the sale reads prices and referral codes from. */
  treasuryObjectId: str(import.meta.env.VITE_TREASURY_OBJECT_ID, ""),

  /**
   * Parent object id of the treasury's referral-code dynamic-field table.
   *
   * Deliberately NOT defaulted. It was unset in the Next.js client too, and its
   * only consumer (`PackageReferrerObjects`, currently not mounted anywhere)
   * passes it straight to a dynamic-field query as a parent id — a plausible
   * string default would produce a confidently wrong lookup instead of an
   * obviously disabled one.
   */
  treasuryReferralCodesField: opt(import.meta.env.VITE_TREASURY_REFERRAL_CODES_FIELD),

  /** Payment coin accepted by the sale (USDC on the active network). */
  coinType: str(import.meta.env.VITE_COIN_TYPE, ""),

  /** $TRUTH package + full coin type, used to read reward balances. */
  truthCoinPackageId: str(import.meta.env.VITE_TRUTH_COIN_PACKAGE_ID, ""),
  truthCoinType: str(import.meta.env.VITE_TRUTH_COIN_TYPE, ""),

  /** Token Generation Event. Claim windows are all offsets from this instant. */
  tgeStartDate: date(import.meta.env.VITE_TGE_START_DATE),

  // `scoreProgressAsymptote` (`VITE_SCORE_PROGRESS_ASYMPTOTE`, née
  // `VITE_SEASON_PROGRESS_ASYMPTOTE`) and `stakingDashboardUrl`
  // (`VITE_STAKING_DASHBOARD_URL`) were removed on 2026-09-28. Their only reader
  // was the draw-window score card, deleted with the draws.

  /**
   * Public origin of THIS app, used to build shareable referral links. Was
   * `NEXT_PUBLIC_RAILWAY_PUBLIC_DOMAIN`. Falls back to the browser's own origin
   * so a local build still produces a link that works.
   */
  siteUrl: str(
    import.meta.env.VITE_SITE_URL,
    typeof window === "undefined" ? "" : window.location.origin,
  ),

  /**
   * Absolute origin of `apps/api`. Empty means same-origin, which is the normal
   * setup: the Vite dev proxy and the production reverse proxy both put the API
   * behind this app's origin, exactly as the Next.js `app/api/**` routes were.
   */
  apiBaseUrl: str(import.meta.env.VITE_API_URL, "").replace(/\/+$/, ""),

  /**
   * DEV-ONLY wallet impersonation (`src/lib/sui/impersonationWallet.ts`).
   * Comma-separated SUI addresses exposed as the accounts of a read-only
   * "Impersonate (dev)" wallet, so a local browser can render the dashboard as
   * any user. `?impersonate=0x…,0x…` adds more at runtime.
   *
   * `import.meta.env.DEV` is `false` in every `vite build`, so a staging or
   * production bundle always sees `enabled: false` whatever the variable says.
   */
  impersonation: {
    enabled: import.meta.env.DEV,
    addresses: import.meta.env.DEV
      ? (opt(import.meta.env.VITE_IMPERSONATE_ADDRESSES) ?? "")
          .split(",")
          .map((address) => address.trim())
          .filter((address) => address.length > 0)
      : [],
  },
} as const;

/** Resolved browser configuration. Never read `import.meta.env` elsewhere. */
export type Config = typeof config;
